import { Spinner } from '@danfoss/webex-ui';
import { CircularProgress, Container, Grid } from '@material-ui/core';
import parse from 'html-react-parser';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { confirmEmailChange } from '../../../api/email';
import { useSharedStyles } from '../../../sharedStyles/Styles';
import { useLogOutCurrentUser } from '../../../utils/useLogOutCurrentUser';
import Footer from '../../Footer/Footer';
import LogoAndBackground from '../../Header/LogoAndBackground';
import Information from '../../Information/Information';
import { PasswordField } from '../../RenderFields/PasswordField';
import { LoginButton } from '../LoginButton/LoginButton';
import { useTokenData } from './useTokenData';

import styles from './EmailChangeConfirmationBase.module.scss';

const passwordSchema = z.object({
  password: z.string().min(1),
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export interface EmailChangeConfirmationTexts {
  confirmHeader: string;
  confirmBody: string;
}

interface EmailChangeConfirmationBaseProps {
  texts: EmailChangeConfirmationTexts;
  invalidContent: React.ReactNode;
}

export const EmailChangeConfirmationBase: React.FC<EmailChangeConfirmationBaseProps> = ({ texts, invalidContent }) => {
  const sharedClasses = useSharedStyles();
  const { t } = useTranslation();
  const [error, setError] = React.useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const {
    register,
    handleSubmit: hookHandleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: 'onChange',
  });

  const passwordField = register('password');

  const isLoggedOut = useLogOutCurrentUser();

  const { isLoading, isValid: isTokenValid, oldEmail, newEmail, token } = useTokenData();

  const onSubmit = async (data: PasswordFormData) => {
    try {
      setError(null);
      await confirmEmailChange(token, data.password);
      setIsConfirmed(true);
    } catch (error) {
      console.error('Error confirming email change:', error);
      setError(t('email_change.confirm.invalid_credentials'));
    }
  }

  if (isLoading || !isLoggedOut) {
    return <Spinner visible />;
  }

  const renderContent = () => {
    if (!isTokenValid) {
      return <>{invalidContent}</>;
    }

    if (isConfirmed) {
      return (
        <Information
          headerText={t('email_change.successfully_changed.header')}
          bodyText={(
            <div className={styles.mainText}>
              {parse(t('email_change.successfully_changed.body', { email: newEmail }))}
            </div>
          )}
          endAdornment={<LoginButton />}
        />
      );
    }

    return (
      <form onSubmit={hookHandleSubmit(onSubmit)} noValidate>
        <Information
          headerText={texts.confirmHeader}
          bodyText={(
            <Grid container spacing={2}>
              <Grid item xs={12} className={styles.mainText}>
                {parse(texts.confirmBody)}
              </Grid>
              <Grid item xs={12} className={styles.mainText}>
                <strong>{t('email_change.confirm.old_email')}</strong>
                <br />
                <span>{oldEmail}</span>
              </Grid>
              <Grid item xs={12} className={styles.mainText}>
                <strong>{t('email_change.confirm.new_email')}</strong>
                <br />
                <span>{newEmail}</span>
              </Grid>
              <Grid item xs={12}>
                <PasswordField
                  name='password'
                  error={error}
                  register={{
                    ...passwordField,
                    onChange: (event) => {
                      if (error) {
                        setError(null);
                      }

                      return passwordField.onChange(event);
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                {t('email_change.confirm.helper_text')}
              </Grid>
            </Grid>
          )}
          buttonText={t('button.confirm')}
          buttonProps={{
            disabled: !isValid || isSubmitting,
            startIcon: isSubmitting && <CircularProgress size={16} color='inherit' />,
          }}
          endAdornment={<LoginButton />}
        />
      </form>
    );
  }

  return (
    <>
      <LogoAndBackground classes={sharedClasses} />
      <Container className={sharedClasses.containerRoot}>
        <Container maxWidth="md" className={`${sharedClasses.outerContainer} ${sharedClasses.relativeContainer}`}>
          <Grid container className={sharedClasses.contentContainerRoot}>
            <Grid item xs={12}>
              {renderContent()}
            </Grid>
          </Grid>
        </Container>
      </Container>
      <Footer contentClass={sharedClasses.footerContent} />
    </>
  );
}
